/**
 * ADR-0098 §8.2 — Subscriptions list.
 *
 * `/app/schedule/subscriptions` — table view with Name / Target / Cadence /
 * Audiences / Owner / Status / Last fired / Last result. New-button →
 * wizard; row click → detail.
 */

import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiFetch } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { LoadingState, ErrorState } from "../components/States.js";

type SubscriptionListItem = {
  id: string;
  name: string;
  page_id: string | null;
  page_title: string | null;
  /** ADR-0216: what it delivers — a page or an Atlas Report. */
  target: { kind: "page" | "atlas_report"; id: string | null; title: string };
  owner_account_id: string;
  owner_display_name: string | null;
  cadence: "DAILY" | "WEEKLY" | "MONTHLY" | "YEARLY";
  cadence_config: Record<string, unknown>;
  paused: boolean;
  delivery_count: number;
  last_fired_at: string | null;
  last_run_status: string | null;
  updated_at: string;
};

function formatCadence(item: SubscriptionListItem): string {
  const cfg = item.cadence_config;
  const time = typeof cfg.time === "string" ? cfg.time : "";
  switch (item.cadence) {
    case "DAILY":
      return `Daily at ${time}`;
    case "WEEKLY": {
      const days = Array.isArray(cfg.days) ? (cfg.days as string[]).join("/") : "";
      return `Weekly ${days} at ${time}`;
    }
    case "MONTHLY":
      return cfg.day === "LAST" ? `Monthly on last day at ${time}` : `Monthly day ${cfg.day} at ${time}`;
    case "YEARLY":
      return `Yearly month ${cfg.month} day ${cfg.day} at ${time}`;
    default:
      return item.cadence;
  }
}

function statusPill(item: SubscriptionListItem): JSX.Element {
  if (item.paused) return <span className="pill pillWarn">Paused</span>;
  return <span className="pill pillOk">Active</span>;
}

function lastResultPill(status: string | null): JSX.Element {
  if (!status) return <span className="muted">—</span>;
  if (status === "succeeded") return <span className="pill pillOk">✓ succeeded</span>;
  if (status === "partial_failure") return <span className="pill pillWarn">⚠ partial</span>;
  if (status === "failed") return <span className="pill pillBad">✗ failed</span>;
  if (status === "running") return <span className="pill">⟳ running</span>;
  if (status === "no_recipients") return <span className="pill pillWarn">⚠ no recipients</span>;
  // ADR-0154 §6.3 C8 — `cancelled` is a real run status (hub RUN_STATUSES) and
  // was falling through to the raw-value pill.
  if (status === "cancelled") return <span className="pill">⊘ cancelled</span>;
  return <span className="pill">{status}</span>;
}

/** ADR-0216 §3.4: the page's title, or the report's name with a small "report" pill. */
export function targetCell(target: SubscriptionListItem["target"]): JSX.Element {
  if (target.kind === "atlas_report") {
    return (
      <>
        <Link to={`/app/workspace/atlas-reports/${encodeURIComponent(target.id ?? "")}`}>{target.title}</Link>{" "}
        <span className="pill pillNeutral">report</span>
      </>
    );
  }
  return <Link to={`/app/build/pages/${encodeURIComponent(target.id ?? "")}`}>{target.title}</Link>;
}

function fmtTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

export function TenantSubscriptions() {
  const nav = useNavigate();
  const [items, setItems] = useState<SubscriptionListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<{ items: SubscriptionListItem[]; next_cursor: string | null }>(
        "/api/tenant/subscriptions",
      );
      setItems(data.items);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <PageLayout
      title="Subscriptions"
      description="Scheduled email distribution of published pages and Atlas Reports."
      actions={
        <button
          className="btn btnPrimary"
          type="button"
          onClick={() => nav("/app/schedule/subscriptions/new")}
        >
          New subscription
        </button>
      }
    >
      <div className="card adminList">
        {error ? <ErrorState detail={error} /> : null}
        {!error && loading ? <LoadingState /> : null}
        {!error && !loading && items.length === 0 ? (
          <div className="adminListEmpty">
            No subscriptions yet. Create one to schedule a page or an Atlas Report for delivery.
          </div>
        ) : null}
        {!error && !loading && items.length > 0 ? (
          <div className="tableWrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Target</th>
                  <th>Cadence</th>
                  <th>Audiences</th>
                  <th>Owner</th>
                  <th>Status</th>
                  <th>Last fired</th>
                  <th>Last result</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <Link className="adminListName" to={`/app/schedule/subscriptions/${row.id}`}>
                        {row.name}
                      </Link>
                    </td>
                    <td>{targetCell(row.target)}</td>
                    <td className="muted">{formatCadence(row)}</td>
                    <td>{row.delivery_count}</td>
                    <td>{row.owner_display_name ?? "(unknown)"}</td>
                    <td>{statusPill(row)}</td>
                    <td className="muted">{fmtTime(row.last_fired_at)}</td>
                    <td>{lastResultPill(row.last_run_status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </PageLayout>
  );
}
